import React, { useMemo } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';

import { EmptyState, Text } from '~/components/core';
import { formatStat } from '~/constants/strategyConstants';
import { useStyles } from '~/hooks';
import { FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { formatStrategyDateTime } from '../../utils/format';
import { buildTradeRows, StrategyResultView, TradeRow, withCurrency } from '../../utils/results';

type Props = {
    results: StrategyResultView;
};

type Styles = ReturnType<typeof stylesGenerator>;

export default function TradesTab({ results }: Props) {
    const styles = useStyles(stylesGenerator);
    // Newest trade on top (the engine returns them chronologically).
    const rows = useMemo(() => buildTradeRows(results.response, results.accountBalances).reverse(), [results]);

    if (rows.length === 0) {
        return <EmptyState title="ai-strategy-no-trades" />;
    }

    return (
        <FlatList
            style={styles.container}
            contentContainerStyle={styles.content}
            data={rows}
            keyExtractor={(item) => String(item.index)}
            initialNumToRender={20}
            renderItem={({ item }) => (
                <TradeCard
                    trade={item}
                    styles={styles}
                    instrumentCurrency={results.instrumentCurrency}
                    accountCurrency={results.accountCurrency}
                />
            )}
        />
    );
}

const TradeCard = ({
    trade,
    styles,
    instrumentCurrency,
    accountCurrency,
}: {
    trade: TradeRow;
    styles: Styles;
    instrumentCurrency: string;
    accountCurrency: string;
}) => {
    const resultPositive = (trade.result ?? 0) >= 0;
    // "+19.52 USD (15.96 CHF)" — the engine's quote-currency figure, then the account-currency
    // restatement when the strategy carries a frozen rate.
    const resultText = withCurrency(`${resultPositive ? '+' : ''}${formatStat(trade.result)}`, instrumentCurrency);
    const accountText =
        typeof trade.resultAccount === 'number' && Number.isFinite(trade.resultAccount)
            ? ` (${withCurrency(trade.resultAccount.toFixed(2), accountCurrency)})`
            : '';
    return (
        <View style={styles.tradeCard}>
            <View style={styles.tradeColumn}>
                <View style={styles.tradeLine}>
                    <Text style={styles.tradeLabel}>ai-strategy-trade-open</Text>
                    <Text noTranslate style={styles.tradeValue}>
                        {formatStat(trade.openPrice)}
                    </Text>
                </View>
                <View style={styles.tradeLine}>
                    <Text style={styles.tradeLabel}>ai-strategy-trade-close</Text>
                    <Text noTranslate style={styles.tradeValue}>
                        {formatStat(trade.closePrice)}
                    </Text>
                </View>
                <View style={styles.tradeLine}>
                    <Text style={styles.tradeLabel}>ai-strategy-trade-result</Text>
                    <Text
                        noTranslate
                        style={StyleSheet.flatten([
                            styles.tradeValue,
                            resultPositive ? styles.resultPositive : styles.resultNegative,
                        ])}
                    >
                        {`${resultText}${accountText}`}
                    </Text>
                </View>
            </View>
            <View style={styles.tradeTimeColumn}>
                <Text noTranslate style={styles.tradeTime}>
                    {formatStrategyDateTime(trade.openTime)}
                </Text>
                <Text noTranslate style={styles.tradeTime}>
                    {formatStrategyDateTime(trade.closeTime)}
                </Text>
            </View>
        </View>
    );
};

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        content: { padding: scaleSize(12) },
        tradeCard: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(6),
            padding: scaleSize(10),
            marginBottom: scaleSize(8),
        },
        tradeColumn: { flex: 1 },
        tradeLine: {
            flexDirection: 'row',
            marginVertical: scaleSize(1),
        },
        tradeLabel: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            width: scaleSize(84),
        },
        tradeValue: {
            color: theme.text,
            fontSize: FONT_SIZE_XS,
        },
        resultPositive: { color: theme.text_upPrice },
        resultNegative: { color: theme.text_downPrice },
        tradeTimeColumn: {
            // Top-aligned so the open/close times line up with the open/close rows on the left,
            // rather than floating centered against the taller three-row left column.
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            marginLeft: scaleSize(8),
        },
        tradeTime: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginVertical: scaleSize(1),
        },
    });
